import * as React from 'react';
import { PaymentDetails } from 'fs-api-client';
import { createBillingApi } from '../../utils/createApi';
import { Subtract } from '../../utils/types';
import { LoaderEnhancerProperties, withLoader } from './LoaderEnhancer';
import { errorHandler } from '../../services/errorHandler';
import { fetchAndUpdateAccountSetting } from './ProfileInjector';

export interface BillingDetailsInjectorProperties extends LoaderEnhancerProperties {
    fetchBillingDetails: (showLoader: boolean) => Promise<PaymentDetails>;
    updateBillingDetails: (paymentDetails: PaymentDetails) => Promise<void>;
    paymentDetails: PaymentDetails;
    errorMessage?: string;
}

interface BillingDetailsInjectorState {
    paymentDetails: PaymentDetails | null;
    errorMessage?: string;
}

let cache: PaymentDetails | null = null;

export function injectBillingDetails<P extends BillingDetailsInjectorProperties>(component: React.ComponentType<P>) {
    let loaderName = 'billingDetailsInjector';
    let DynamicComponent: any = withLoader(component, loaderName);

    return class BillingDetailsInjector extends React.Component<Subtract<P, BillingDetailsInjectorProperties>, BillingDetailsInjectorState> {
        private isCompMounted: boolean = false;

        constructor(props: P) {
            super(props);

            this.fetchBillingDetails = this.fetchBillingDetails.bind(this);
            this.updateBillingDetails = this.updateBillingDetails.bind(this);

            this.state = {
                paymentDetails: cache
            };
        }

        componentDidMount() {
            this.isCompMounted = true;
            if (!cache) {
                this.fetchBillingDetails();
            }
        }

        componentWillUnmount() {
            this.isCompMounted = false;
        }

        render() {
            return (
                <DynamicComponent
                    {...this.props}
                    loading={!this.state.paymentDetails}
                    fetchBillingDetails={this.fetchBillingDetails}
                    updateBillingDetails={this.updateBillingDetails}
                    paymentDetails={this.state.paymentDetails}
                    errorMessage={this.state.errorMessage}
                />
            );
        }

        private async fetchBillingDetails(): Promise<PaymentDetails> {
            return new Promise((resolve, reject) => {
                if (this.isCompMounted) {
                this.setState(
                    { errorMessage: undefined },
                    async () => {
                        const api = await createBillingApi();
                        api.getPaymentDetails().then(result => {
                            const details = result.body as PaymentDetails;
                            cache = details;
                            this.setState(
                                { paymentDetails: details },
                                () => {
                                    resolve(details);
                                }
                            );
                        }).catch(e => {
                            errorHandler(e);
                            const errorMessage = 'Could not load details. Check your connection to the server and try again.';
                            this.setState(
                                { errorMessage: errorMessage },
                                () => reject(e));
                        });
                    });
                }
            });
        }

        private async updateBillingDetails(details: PaymentDetails): Promise<void> {
            return new Promise((resolve, reject) => {
                this.setState(
                    { errorMessage: undefined },
                    async () => {
                        const api = await createBillingApi();
                        api.updatePaymentDetails(details).then(() => {
                            cache = details;
                            this.setState(
                                { paymentDetails: details },
                                () => resolve());
                            fetchAndUpdateAccountSetting();
                        }).catch((result) => {
                            const error = resolveApiErrorResult(result);
                            this.setState(
                                { errorMessage: error.message },
                                () => reject(error));
                        });
                    });
            });
        }
    };
}

function resolveApiErrorResult(result: any): any {
    let error: any = result;
    if (result && result.response && result.response.body) {
        error = result.response.body;
    }
    return error;
}



// WEBPACK FOOTER //
// ./src/webportal/components/hocs/BillingDetailsInjector.tsx